import type { CityConfig } from "@/pages/city-template/types";

export const summerlinConfig: CityConfig = {
  city: "Summerlin",
  state: "Nevada",
  stateAbbr: "NV",
  slug: "summerlin",
  route: "/summerlin",
  title: "Emergency & Expedited Passport Services Summerlin NV | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Summerlin, NV — as fast as 24 hours. No Summerlin passport agency. Fast Passport Center couriers to the San Francisco Passport Agency at the Phillip Burton Federal Building, about 570 miles away. A+ BBB rated. Serving Red Rock Canyon, Skye Canyon, and all western Las Vegas Valley travelers heading abroad for business, golf, or family.",
  h1: "Emergency & Expedited Passport Services in Summerlin, NV",
  heroSubtitle: "Summerlin is a 22,500-acre master-planned community on the western edge of the Las Vegas Valley in Clark County — one of the most successful and desirable planned communities in the United States. Framed by the stunning Red Rock Canyon National Conservation Area to the west and the Spring Mountains beyond, Summerlin is home to world-class golf courses including TPC Summerlin, the Las Vegas Ballpark, Downtown Summerlin's shopping and dining district, and a community of professionals, families, and retirees who value quality of life in the desert. Fast Passport Center's registered couriers deliver your documents to the San Francisco Passport Agency at the Phillip Burton Federal Building so you get a new or renewed passport as fast as 24 hours — whether you're a Summerlin executive attending an international conference, a PGA-level golfer heading to an overseas tournament, or a Red Rock hiking enthusiast planning an adventure abroad.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Summerlin%20Nevada%20master-planned%20community%20with%20Red%20Rock%20Canyon%20mountains%20in%20background%20at%20golden%20hour%2C%20modern%20suburban%20landscape%20with%20palm%20trees%20and%20desert%20landscaping%2C%20Downtown%20Summerlin%20shopping%20district%20with%20contemporary%20architecture%20and%20mountain%20backdrop%2C%20wide%20angle%20desert%20photography%20with%20warm%20golden%20sunset%20tones&width=1800&height=700&seq=summerlin-nv-hero-001&orientation=landscape",
  heroImageAlt: "Summerlin Nevada community with Red Rock Canyon mountains at golden hour",
  geoPosition: "36.1369;-115.3188",
  geoPlacename: "Summerlin, Nevada",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "Phillip Burton Federal Building, 450 Golden Gate Avenue, San Francisco, CA 94102",
    distance: "Approximately 570 miles from Summerlin",
  },
  nearbyCitiesZips: [
    { name: "Summerlin", zips: "89135, 89138, 89144, 89134, 89128, 89143, 89145" },
    { name: "The Lakes", zips: "89117" },
    { name: "Skye Canyon", zips: "89143" },
    { name: "Red Rock", zips: "89161" },
    { name: "Centennial Hills", zips: "89131, 89149" },
    { name: "Las Vegas", zips: "89101" },
    { name: "Spring Valley", zips: "89103, 89117, 89118, 89146" },
    { name: "Enterprise", zips: "89113, 89118, 89119" },
    { name: "Blue Diamond", zips: "89004" },
    { name: "Mountain's Edge", zips: "89179" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to San Francisco Passport Agency — about 570 miles from Summerlin" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Western Metro Service", desc: "Serving Summerlin, Red Rock, Skye Canyon, and all western Las Vegas Valley" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Summerlin case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Nevada", route: "/nevada" },
    { name: "Expedited passport in Las Vegas", route: "/las-vegas" },
    { name: "Urgent passport in Summerlin NV", route: "/summerlin" },
    { name: "Nevada passport hub", route: "/nevada" },
    { name: "Fast passport in Henderson NV", route: "/henderson-nv" },
    { name: "Expedited passport in North Las Vegas", route: "/north-las-vegas" },
    { name: "Emergency passport in Sparks NV", route: "/sparks" },
  ],
  fpcOfficeAddress: "355 S Grand Ave, Ste 2450, Los Angeles, California 90071",
  fpcOfficeNote: "Fast Passport Center serves Summerlin remotely via secure courier. Our registered couriers hand-deliver your documents to the San Francisco Passport Agency at the Phillip Burton Federal Building in downtown San Francisco. Summerlin's position in the western Las Vegas Valley — with easy access to Harry Reid International Airport (LAS) — makes overnight courier routing to the Bay Area consistently reliable. Our dedicated agents ensure Summerlin travelers, from TPC Summerlin golf professionals to Downtown Summerlin business executives, never need to fly to San Francisco for passport processing.",
  fpcOfficeDistance: "Serving all of Summerlin, Red Rock, Skye Canyon, and the western Las Vegas Valley metro area",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Summerlin, The Lakes, Red Rock Canyon area, Skye Canyon, Centennial Hills, and all western Las Vegas Valley communities. From TPC Summerlin golf professionals to international business travelers, we help Summerlin residents get their passports fast.",
  nearbyCitiesIntro: "We serve Summerlin and nearby communities including The Lakes, Skye Canyon, Red Rock, Centennial Hills, Spring Valley, and the entire western Clark County metro area.",
  howItWorksIntro: "Most Summerlin travelers complete everything online and ship documents overnight. At approximately 570 miles from San Francisco, Fast Passport Center provides fast courier turnaround for western metro residents — eliminating the need for a round-trip flight to the Bay Area just for passport processing.",
  mistakesIntro: "Summerlin travelers, including golf professionals and international business executives, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Summerlin and western Las Vegas Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Summerlin? Here are the most common ones we hear from western Clark County and Las Vegas Valley travelers.",
  statePageRoute: "/states/nevada",
  statePageName: "Nevada",
  dmvWebsite: "https://dmv.nv.gov/",
  dmvLabel: "Nevada DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins at Golden Gate Avenue",
        "Life-or-death emergencies only",
        "570 miles to San Francisco",
        "Still requires pre-scheduled NPIC appointment",
        "Round-trip flight typically needed",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No flight or appointment needed",
        "Ideal for golf professionals",
        "Perfect for business executives",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from LAS",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Summerlin is 570 miles from the San Francisco Passport Agency — our courier service saves western metro residents the round-trip flight to the Bay Area.",
      bullets: ["Clark County coverage", "24–48 hour turnaround available", "No appointment or flight needed"],
      badge: "Fastest in Western Las Vegas",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Summerlin adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Summerlin and western valley"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Summerlin and Clark County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Summerlin residents with DS-64 form guidance and courier routing to San Francisco.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via San Francisco agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Clark County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Summerlin and Clark County residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Summerlin NV", description: "Urgent passport processing for Summerlin, NV travelers via San Francisco Passport Agency courier." },
    { name: "Passport Renewal Summerlin NV", description: "Fast passport renewal for Summerlin and Clark County, Nevada residents." },
    { name: "New Passport Summerlin NV", description: "First-time passport application assistance for Summerlin and western metro area residents." },
    { name: "Lost Passport Summerlin NV", description: "Passport replacement for lost or stolen passports for Summerlin and Clark County residents." },
    { name: "Child Passport Summerlin NV", description: "Minor passport services for Summerlin and western valley families." },
    { name: "Name Change Passport Summerlin NV", description: "Passport name change service for Summerlin, NV residents." },
  ],
  testimonials: [
    {
      name: "Brad K.",
      location: "Summerlin — TPC Summerlin Golf Professional",
      rating: 5,
      text: "I'm a PGA teaching pro at TPC Summerlin and got a last-minute invitation to compete in a pro-am tournament in Scotland. My passport had expired three months prior. Fast Passport Center processed my renewal in 5 business days. The courier picked up documents right from Summerlin — I never missed a lesson with my students. Played St Andrews two weeks later.",
      service: "Passport Renewal",
    },
    {
      name: "Monica R.",
      location: "Downtown Summerlin — Corporate Executive",
      rating: 5,
      text: "VP at a hospitality company headquartered near Downtown Summerlin. My team was pitching a major client in Tokyo on three weeks' notice. Discovered my passport had 4 months left — Japan requires 6 months validity beyond departure. Fast Passport Center got me a new passport in 6 business days. I led the pitch in Tokyo and we won the account.",
      service: "Passport Renewal",
    },
    {
      name: "The P Family",
      location: "The Vistas, Summerlin",
      rating: 5,
      text: "Family of four in The Vistas neighborhood planning a summer trip to Australia. Two adult renewals plus two first-time child passports — and we needed Australian ETA visas on top. Fast Passport Center processed all four passports in 8 business days, then we handled the ETAs online. The agent coordinated everything and gave us a clear timeline. Sydney was incredible.",
      service: "Family Passport Package",
    },
    {
      name: "Robert D.",
      location: "Skye Canyon, NV",
      rating: 5,
      text: "Retired police officer living in Skye Canyon. Planned a retirement dream trip — motorcycle tour through Patagonia. Booked the tour, realized my passport had less than 5 months left. Fast Passport Center renewed it in 7 business days. The agent was fantastic — called me personally to confirm everything was on track. I rode through the Andes with my new passport in my jacket.",
      service: "Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "sum-1",
      name: "Summerlin Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2000 N Rampart Blvd",
      city: "Las Vegas",
      state: "NV",
      zip: "89128",
      phone: "(702) 363-0516",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Summerlin — Rampart",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility serving Summerlin and the western Las Vegas Valley. Located on Rampart Boulevard. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "sum-2",
      name: "Centennial Hills Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "5455 N Jensen St",
      city: "Las Vegas",
      state: "NV",
      zip: "89129",
      phone: "(702) 396-1909",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Centennial Hills — 5 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Centennial Hills and northwest valley residents. Convenient for Skye Canyon and northern Summerlin communities.",
      icon: "ri-mail-line",
    },
    {
      id: "sum-3",
      name: "Clark County Government Center",
      type: "County Clerk Passport Acceptance",
      address: "500 S Grand Central Pkwy",
      city: "Las Vegas",
      state: "NV",
      zip: "89155",
      phone: "(702) 455-0000",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "Downtown Las Vegas — 12 miles east",
      website: "https://www.clarkcountynv.gov",
      websiteLabel: "Clark County Government",
      notes: "Largest passport acceptance facility in Clark County. Most appointment availability in the valley. About 20 minutes from Summerlin.",
      icon: "ri-government-line",
    },
    {
      id: "sum-4",
      name: "Las Vegas Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1001 E Sunset Rd",
      city: "Las Vegas",
      state: "NV",
      zip: "89199",
      phone: "(702) 361-9224",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Las Vegas — 15 miles southeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest USPS facility in the valley. More appointment slots and extended service. Convenient for southern Summerlin residents via the 215 Beltway.",
      icon: "ri-mail-line",
    },
    {
      id: "sum-5",
      name: "Spring Valley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3315 S Jones Blvd",
      city: "Las Vegas",
      state: "NV",
      zip: "89146",
      phone: "(702) 873-8490",
      hours: "Mon–Fri: 9:00 AM – 3:30 PM",
      distance: "Spring Valley — 8 miles southeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Summerlin residents near the Summerlin-Spring Valley border. Good alternative if Rampart appointments are full.",
      icon: "ri-mail-line",
    },
    {
      id: "sum-6",
      name: "Aliante Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "7051 N Aliante Pkwy",
      city: "North Las Vegas",
      state: "NV",
      zip: "89084",
      phone: "(702) 399-2875",
      hours: "Mon–Fri: 9:00 AM – 3:30 PM",
      distance: "Aliante — 10 miles northeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Northern valley option. Convenient for Skye Canyon residents. Newer facility with generally shorter wait times.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to SF Agency", agency: "570 miles — round-trip flight to SFO plus downtown SF transit", fpc: "We fly for you — no travel needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-golf-ball-line",
      title: "Golf Professionals Delaying Passport Renewal Until Tournament Invitation Arrives",
      description: "Summerlin is home to TPC Summerlin and a major golf destination. PGA and LPGA professionals, teaching pros, and competitive amateurs often receive international tournament invitations on short notice — sometimes 2–4 weeks before the event. An expired or nearly-expired passport at invitation time means scrambling. Golf professionals should maintain valid passports year-round as part of their career toolkit.",
    },
    {
      icon: "ri-sun-line",
      title: "Desert Heat Damaging Passport Documents Left in Golf Bags or Vehicles",
      description: "Summerlin summer temperatures routinely exceed 110°F. Passport photos, application forms, and existing passports left in hot vehicles or golf bags can warp, fade, or become damaged. Always keep passport documents in climate-controlled environments. If mailing documents during summer, use air-conditioned drop boxes — courier pickup with climate-controlled transport is recommended.",
    },
    {
      icon: "ri-global-line",
      title: "Passport Book vs. Passport Card Confusion for International Flights from LAS",
      description: "Some Summerlin residents who frequently cross the border into Mexico by land (a 5-hour drive to Yuma or Tijuana) use a passport card for convenience. Passport cards cannot be used for international air travel from LAS. If your itinerary involves flying internationally — even to Mexico or Canada — you must present a valid passport book at check-in.",
    },
    {
      icon: "ri-building-line",
      title: "Assuming the Las Vegas Strip or Downtown Has a Passport Agency",
      description: "Given Las Vegas's status as an international destination, many Summerlin residents assume there must be a passport agency somewhere in the valley. There is not. The closest passport agency is in San Francisco — 570 miles away. Any local office advertising 'same-day passports' in Las Vegas is a courier service, not a government passport agency.",
    },
    {
      icon: "ri-calendar-line",
      title: "Scheduling International Travel During CES or SEMA When LAS Is at Peak Capacity",
      description: "Las Vegas hosts massive conventions (CES in January, SEMA in November, NAB in April) that draw hundreds of thousands of international attendees. During these events, LAS is at maximum capacity and overnight courier volumes spike. Summerlin travelers who realize just before a convention that their passport needs renewal face compressed timelines. Start the passport process at least 6 weeks before major convention-season international travel.",
    },
    {
      icon: "ri-passport-line",
      title: "Not Checking Six-Month Passport Validity for Asian and European Business Travel",
      description: "Summerlin's professional community — from Downtown Summerlin corporate offices to hospitality executives — regularly travels to Asia and Europe for business. Many Asian countries (China, Japan, Thailand, Indonesia) and the Schengen Area in Europe require 6 months passport validity beyond your intended stay. Business travelers who check only the expiration date (not the 6-month buffer) risk being denied boarding at LAS.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location on W Sahara Ave or W Charleston Blvd in Summerlin." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the San Francisco Passport Agency — overnight from LAS." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the San Francisco Passport Agency via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Summerlin or western valley address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Summerlin from the closest passport agency?",
      a: "Summerlin is approximately 570 miles from the closest passport agency, the San Francisco Passport Agency at the Phillip Burton Federal Building on Golden Gate Avenue. There is no passport agency anywhere in Nevada. Getting an emergency appointment typically requires a round-trip flight to SFO, making courier service the far more practical option for Summerlin residents.",
    },
    {
      q: "Where can I get passport photos in Summerlin?",
      a: "Walgreens at 1800 N Buffalo Dr (near Summerlin Hospital) and CVS at 8080 W Sahara Ave (near Boca Park) offer passport photo services. For Downtown Summerlin visitors, the Walgreens at 7225 S Durango Dr is most convenient. All must meet 2x2 inch white background specifications with no glasses. Verify photos before submitting.",
    },
    {
      q: "How fast can I get a passport in Summerlin?",
      a: "Fast Passport Center's emergency service processes passports in 24–48 hours for Summerlin residents via courier to San Francisco. Standard expedited service runs 2–9 business days. LAS airport's overnight cargo infrastructure — accessible via the 215 Beltway from Summerlin in about 25 minutes — makes courier routing to San Francisco consistently reliable.",
    },
    {
      q: "Can I get a passport at the Summerlin Post Office on Rampart?",
      a: "Yes. The Summerlin Post Office at 2000 N Rampart Blvd accepts passport applications as a USPS passport acceptance facility. However, it does not process or expedite passports — it only accepts applications and forwards them for standard processing (6–10 weeks). Call (702) 363-0516 for an appointment. Walk-ins are not accepted.",
    },
    {
      q: "I live in Skye Canyon — where is the closest passport acceptance facility?",
      a: "Skye Canyon residents have several nearby options. The Centennial Hills Post Office at 5455 N Jensen St (about 5 miles east) and the Aliante Post Office at 7051 N Aliante Pkwy (about 8 miles northeast) both accept passport applications. For more appointment availability, the Clark County Government Center downtown is about 20 miles away.",
    },
    {
      q: "Does Red Rock Canyon have any passport services?",
      a: "No. Red Rock Canyon is a National Conservation Area with no passport facilities. The closest acceptance facilities to the Red Rock area are the Summerlin Post Office at 2000 N Rampart Blvd (about 8 miles east) and the Centennial Hills Post Office at 5455 N Jensen St (about 12 miles north).",
    },
    {
      q: "I'm playing in an overseas golf tournament through TPC Summerlin. Can you help with rush processing?",
      a: "Absolutely. Summerlin golf professionals and competitive amateurs regularly use Fast Passport Center for rush passport processing before international tournaments. Our 2–9 business day courier service handles document review, photo verification, and secure submission — ideal when tournament invitations arrive on short notice. We've helped TPC Summerlin players get to tournaments across the UK, Japan, Australia, and the Middle East.",
    },
    {
      q: "I live in The Vistas — can you pick up documents from my Summerlin home?",
      a: "Fast Passport Center provides secure overnight document shipping labels you can use at any UPS location in Summerlin. For The Vistas residents, the UPS Store at 10890 W Charleston Blvd (near the 215) and the UPS Store at 1825 N Buffalo Dr are the most convenient drop-off points. Our courier service ensures door-to-door tracking from Summerlin to the San Francisco Passport Agency.",
    },
    {
      q: "Do I need a REAL ID from the Nevada DMV to get a passport?",
      a: "No. A REAL ID is not required for passport applications. A passport itself is already REAL ID-compliant and can be used for domestic flights. You need a valid government-issued photo ID (such as a Nevada driver's license) to apply for a passport at an acceptance facility. The closest DMV to Summerlin is the NV DMV Flamingo office at 8250 W Flamingo Rd.",
    },
    {
      q: "I'm a corporate executive at Downtown Summerlin who travels to Asia monthly. How do I avoid passport gaps?",
      a: "Frequent international business travelers in Summerlin should track their passport's expiration date and the 6-month validity rule proactively. Set a calendar reminder 9 months before expiration to start the renewal process. Fast Passport Center's expedited service ensures minimal downtime — 2–9 business days means you can renew between trips without disrupting your travel schedule.",
    },
    {
      q: "Is overnight courier reliable from Summerlin to San Francisco during summer heat?",
      a: "Yes. Overnight courier from Las Vegas to San Francisco operates reliably year-round, including during the hottest summer months when temperatures regularly exceed 110°F. LAS airport handles significant overnight cargo volume regardless of temperature, and our courier partners use climate-controlled transport for sensitive documents. The LV-to-SF corridor has multiple daily flights — weather is almost never a factor.",
    },
    {
      q: "Can I use a passport card instead of a passport book for a flight from LAS to Mexico or Canada?",
      a: "No. Passport cards are strictly limited to land and sea border crossings — they are not valid for international air travel from LAS or any other airport. Many Summerlin residents with passport cards (from frequent I-15 drives to Mexico) discover this at the gate. If you're flying internationally for any reason, you must have a valid U.S. passport book.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Buffalo & Summerlin Pkwy", address: "1800 N Buffalo Dr, Las Vegas, NV 89128", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Sahara & Town Center", address: "8080 W Sahara Ave, Las Vegas, NV 89117", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — Durango & 215", address: "7225 S Durango Dr, Las Vegas, NV 89113", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Lake Mead & Rampart", address: "1900 N Rampart Blvd, Las Vegas, NV 89128", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Charleston & 215", address: "10890 W Charleston Blvd, Las Vegas, NV 89135", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–2PM" },
    { name: "FedEx Office — Flamingo & 215", address: "9380 W Flamingo Rd, Las Vegas, NV 89147", hours: "Mon–Fri: 7:30AM–7PM, Sat: 9AM–5PM" },
  ],
  dmvLocations: [
    { name: "NV DMV — Flamingo", address: "8250 W Flamingo Rd, Las Vegas, NV 89147", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Largest DMV in the Las Vegas Valley and closest to Summerlin. REAL ID, driver's license, and state ID services. About 5 miles south of Downtown Summerlin.", featured: true },
    { name: "NV DMV — North Decatur", address: "4110 N Decatur Blvd, Las Vegas, NV 89130", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Northern valley DMV. Convenient for Skye Canyon and northern Summerlin residents. About 10 miles northeast.", featured: false },
    { name: "NV DMV — Henderson", address: "1399 American Pacific Dr, Henderson, NV 89074", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Southeastern valley DMV. About 25 miles from Summerlin via the 215 Beltway.", featured: false },
  ],
  airports: [
    {
      code: "LAS",
      name: "Harry Reid International Airport",
      distance: "15 miles southeast of Summerlin",
      address: "5757 Wayne Newton Blvd, Las Vegas, NV 89119",
      note: "The primary international gateway for all Southern Nevada. Direct flights to Europe, Asia, and the Americas. About 25 minutes from Summerlin via the 215 Beltway. Major international routes from LAS serve London, Seoul, Amsterdam, Mexico City, Toronto, and more.",
      flightUrl: "https://www.harryreidairport.com/",
      flightLabel: "LAS Airport",
      featured: true,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "570 miles northwest — co-located with SF Passport Agency",
      address: "San Francisco, CA 94128",
      note: "Co-located with the closest passport agency. Summerlin travelers with confirmed NPIC emergency appointments would need a flight to SFO — multiple daily non-stops from LAS take about 1 hour 30 minutes.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Airport",
      featured: false,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "270 miles southwest of Summerlin",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Second closest major international gateway. About 4–5 hours driving or a 1-hour flight from LAS. Home to the Los Angeles Passport Agency — another emergency processing option via courier.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Airport",
      featured: false,
    },
    {
      code: "HND",
      name: "Las Vegas-Henderson Executive Airport",
      distance: "20 miles south of Summerlin",
      address: "3500 Executive Terminal Dr, Henderson, NV 89052",
      note: "General aviation and private jet airport popular with Summerlin's executive travelers. No commercial international service, but convenient for private international charters requiring valid passports.",
      flightUrl: "https://www.hnd.aero/",
      flightLabel: "HND Airport",
      featured: false,
    },
  ],
};